import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Clock,
  DollarSign,
  FileText,
  PlusCircle,
  Briefcase,
  XCircle,
  Star,
} from "lucide-react";
import { Project } from "@/types/project";
import { useJobStore } from "@/store/jobStore";
import { capitalizeFirst, dateFormat, getImageUrl } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import { JobDetail, useJobApplyStore } from "@/store/jobApplyStore";
import { FileUploadInput } from "@/components/FileUploadInput";
import { authHeaders, Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { APPLICATION_STATUS_WITH_KEY_MAPPING, DURATION_TYPE } from "@/utils/constant";
import CircularDotsLoader from "@/components/common/CircularLoader";
import { ApplicationStatus } from "@/utils/enum";
import Profile from "./Profile";

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [newMessage, setNewMessage] = useState("");
  const [isWithdrawApplicationOpen, setIsWithdrawApplicationOpen] = useState(false);
  const [isShowLoader, setIsShowLoader] = useState<boolean>(false);
  const [applyJobData, setApplyJobData] = useState({
    jobId: "",
    freelancerId: "",
    userId: "",
    coverLetterText: "",
    coverLetterFileUrl: "",
    proposedBudget: "",
    coverLetterFile: undefined as File | undefined,
  });

  const [enterpriseProfileData, setEnterpriseProfileData] = useState(null)
  const [isEnterpriseOpen, setIsEnterpriseOpen] = useState(false)

  const { profile, applyJob } = useJobStore();
  const { user } = useUserStore();
  const { jobApplications, jobdetails, fetchJobApplications, updateApplicationStatus, fetchJobDetailsByFreelancer } =
    useJobApplyStore();
    
  const [projectDetails, setProjectDetails] = useState({
    tasks: [
      { id: 1, title: "Smart Contract Development", status: "completed" },
      { id: 2, title: "Frontend Integration", status: "in-progress" },
      { id: 3, title: "Security Audit", status: "pending" },
      { id: 4, title: "Documentation", status: "in-progress" },
      { id: 5, title: "User Testing", status: "pending" },
    ],
    team: [
      {
        id: 1,
        name: "Alex Johnson",
        role: "Lead Developer",
        avatar: "/placeholder.svg",
      },
      {
        id: 2,
        name: "Maria Rodriguez",
        role: "Blockchain Expert",
        avatar: "/placeholder.svg",
      },
      {
        id: 3,
        name: "David Kim",
        role: "Frontend Developer",
        avatar: "/placeholder.svg",
      },
    ],
    updates: [
      {
        id: 1,
        content: "Completed the first milestone of development",
        date: "Sept 30, 2023",
        user: "Alex Johnson",
      },
      {
        id: 2,
        content: "Frontend dashboard implemented with real-time data",
        date: "Oct 15, 2023",
        user: "David Kim",
      },
      {
        id: 3,
        content: "Integrated security protocols for asset management",
        date: "Nov 2, 2023",
        user: "Maria Rodriguez",
      },
    ],
    conversations: [
      {
        id: 1,
        sender: "Client",
        message: "Can we get an update on the integration?",
        time: "2 days ago",
      },
      {
        id: 2,
        sender: "Team",
        message:
          "We've completed about 70% of the integration. There were some challenges with the API that we've resolved.",
        time: "1 day ago",
      },
      {
        id: 3,
        sender: "Client",
        message: "Great! When can we expect to see a demo?",
        time: "1 day ago",
      },
      {
        id: 4,
        sender: "Team",
        message: "We can arrange a demo by the end of this week.",
        time: "12 hours ago",
      },
    ],
  });

  const getData = async () => {
    await fetchJobDetailsByFreelancer(id, user?.userId, user?.accessToken)
  }

  const reviews = jobdetails?.enterprise?.reviews || [];

  const avgRatingRaw =
    reviews.reduce((acc, review) => acc + review.rating, 0) / (reviews.length || 1);

  const avgRating = Math.round(avgRatingRaw * 2) / 2;

  useEffect(() => {
    // Find the project by ID
    if (id) {
      getData()
      const foundProject = profile && profile?.results?.find((p) => p?.job?._id === id);
      if (foundProject) {
        setProject({
          id: parseInt(foundProject._id),
          title: foundProject?.job?.title || "Untitled Project",
          client: foundProject?.job?.enterpriseId?.companyName || "Unknown Client",
          status: "Pending",
          deadline: foundProject?.job?.duration || "No Deadline",
          budget: (foundProject?.job?.budget || 0).toString(),
          duration: foundProject?.job?.duration || "N/A",
          durationUnit: jobdetails?.job?.durationUnit || 0,
          durationValue: jobdetails?.job?.durationValue || 0,
          skills: foundProject?.job?.requiredSkills || [],
          description: foundProject?.job?.description || "No description available",
          category: foundProject?.job?.category || "",
          clientInfo: {
            name: foundProject?.job?.enterpriseId?.companyName,
            rating: foundProject?.job?.enterpriseId?.rating || 0,
            location:
              foundProject?.job?.enterpriseId?.location?.address +
              ", " +
              foundProject?.job?.enterpriseId?.location?.city +
              ", " +
              foundProject?.job?.enterpriseId?.location?.country || "Unknown",
            jobsPosted: foundProject?.job?.enterpriseId?.createdAt || "",
            verified: false,
          },
          postedDate: "Unknown",
          complexity: "Unknown",
          icon: <Briefcase size={20} />,
          proposals: 0,
          views: 0,
        });
        setApplyJobData({
          ...applyJobData,
          freelancerId: user?.userId,
          jobId: id,
          userId: user?.userId,
          proposedBudget: foundProject?.job?.budget
        });
      } else {
        toast({
          title: "Project not found",
          variant: "destructive",
        });
        navigate("/projects");
      }
    }

    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    setProject({
      ...project, 
      durationUnit: jobdetails?.job?.durationUnit || 0,
      durationValue: jobdetails?.job?.durationValue || 0,
    })
  }, [jobdetails])

  const handleTaskStatusChange = (taskId: number, newStatus: string) => {
    const updatedTasks = projectDetails.tasks.map((task) =>
      task.id === taskId ? { ...task, status: newStatus } : task
    );

    setProjectDetails({
      ...projectDetails,
      tasks: updatedTasks,
    });

    // toast.success(`Task status updated to ${newStatus}`);
  };

  const handleCancelProject = () => {
    // toast.success("Project has been cancelled");
    setIsConfirmationDialogOpen(false);
    navigate("/projects");
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const newConversation = {
      id: projectDetails.conversations.length + 1,
      sender: "Team",
      message: newMessage,
      time: "Just now",
    };

    setProjectDetails({
      ...projectDetails,
      conversations: [...projectDetails.conversations, newConversation],
    });

    setNewMessage("");
    // toast.success("Message sent");
  };

  const handleAddUpdate = () => {
    // toast.success("Update added successfully");
  };

  const handleApplyProject = async () => {
    // if (applyJobData.coverLetterFile && applyJobData.proposedBudget === "" || applyJobData.coverLetterText === "" && applyJobData.proposedBudget === "") {
    //   toast({
    //     title: "Cover letter text and budget is required.",
    //     variant: "destructive",
    //   });
    //   return;
    // }

    try {
      await applyJob(id, applyJobData, user?.accessToken);
      setIsApplyDialogOpen(false);
      toast({
        title: "Job Apply successfully!",
        variant: "success",
      });
      getData()
    } catch (err) {
      toast({
        title: 'The proposed budget is required. Please provide a valid amount',
        variant: "destructive",
      });
    }
  };

  const showwithdrawApplicationModal = () =>{
    setIsWithdrawApplicationOpen(true);
  }

  const withdrawApplication = async(jobdetails: JobDetail) => {
    try {
      setIsShowLoader(true);
      await Axios.put(
        `${API_END_POINTS.WITHDRAW_APPLICATION}/${jobdetails.application._id}/withdraw`,
        {freelancerId: user.userId},
        authHeaders(user?.accessToken)
      );
      setIsShowLoader(false);
      setIsWithdrawApplicationOpen(false)
      toast({
        description: "Application withdrawn successfully",
        variant: "success",
      });
    } catch (err: any) {
      setIsShowLoader(false);
      setIsWithdrawApplicationOpen(false)
      toast({
        title:"Failed to withdrawn application, Please try again.",
        variant: "destructive",
      });
    }
  }

  const handleClientProfile = (enterprise: any) => {
    setIsEnterpriseOpen(true)
    setEnterpriseProfileData(enterprise)
  }
  const backToProject = () => {
    setIsEnterpriseOpen(false)
    setEnterpriseProfileData(null)
  }


  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-7xl">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto p-4 max-w-7xl">
        <div className="flex flex-col items-center justify-center h-96">
          <FileText className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The project you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/projects")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const updateStatus = async (id: string, status: any) => {
    try {
      const res = await updateApplicationStatus(id, status, user?.accessToken);
      toast({
        title: "Status updated successfully",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Failed to update job applications status. please try again",
        variant: "destructive",
      });
    }
  };

  const handleTabChange = async (tab: string) => {
    setActiveTab(tab);
    if (tab === "application") {
      await fetchJobApplications(id, user?.accessToken);
    }
  };

  const uploadResumeAPI = async (file: File): Promise<any> => {
    // setLoading(true);
    const formData = new FormData();
    formData.append("coverletter", file);

    try {
      const res = await Axios.post(
        `${API_END_POINTS.UPLOAD_JOB_APPLICATION_COVER_LETTER}?userId=${user?.userId}&uploadType=job`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setProgress(percent);
          },
        }
      );
      setLoading(false);
      toast({
        title: "Cover letter uploaded",
        description: "Cover letter uploaded successfully.",
        variant: "default",
      });
      return res.data;
    } catch (error) {
      setLoading(false);
      toast({
        title: "Error",
        description: "Failed to upload resume. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string | number) => {
    switch (Number(status)) {
      case 2 :
      case 4 :  
        return "bg-green-100 text-green-700 border-green-200";
      case 5 :
        return "bg-blue-100 text-blue-700 border-blue-200";
      case 3 :
        return "bg-purple-100 text-purple-700 border-purple-200";
      case 6 :
        return "bg-purple-100 text-red-700 border-red-200";
      case 7 :
        return "bg-purple-100 text-orange-700 border-orange-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Back button and Project Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/projects")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Projects
        </Button>

        {isShowLoader && <CircularDotsLoader className="absolute" text="Loading...." />}

        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{jobdetails?.job?.title}</h1>
          </div>

          <div className="flex space-x-2">
            {/* <AlertDialog
              open={isConfirmationDialogOpen}
              onOpenChange={setIsConfirmationDialogOpen}
            >
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <X className="mr-1 h-4 w-4" /> Cancel Project
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cancelling this project will notify all team members and the
                    client. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsConfirmationDialogOpen(false)}
                  >
                    Go Back
                  </Button>
                  <Button variant="destructive" onClick={handleCancelProject}>
                    Cancel Project
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog> */}


            {user?.role === "freelancer" && jobdetails && (
              <>
                {jobdetails.status === 0 && !jobdetails?.application ? (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setIsApplyDialogOpen(true)}
                  >
                    Apply
                  </Button>
                ) : (<div className="flex flex-col gap-2">
                  <div className="flex justify-end">
                    <Badge variant="outline" className={getStatusColor(jobdetails.application?.status)}>
                      {APPLICATION_STATUS_WITH_KEY_MAPPING[jobdetails.application?.status] || "Unknown Status"}
                    </Badge>
                  </div>
                  {/* {(jobdetails.application?.status !== ApplicationStatus.REJECTED && jobdetails.application?.status !== ApplicationStatus.ACCEPTED) && <Button
                    variant="outline" 
                    onClick={() => showwithdrawApplicationModal()}
                    size="sm" >
                    <XCircle className="mr-1 h-3 w-3" />
                    Withdraw Application
                  </Button>} */}
                  <Dialog open={isWithdrawApplicationOpen} onOpenChange={setIsWithdrawApplicationOpen}>
                      <DialogContent className="w-[400px]">
                        <DialogHeader className="pt-5 pb-2">
                          <DialogTitle>Do you really want to Withdraw Application?</DialogTitle>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsWithdrawApplicationOpen(false)}>
                            No
                          </Button>
                          <Button variant="outline" onClick={() => withdrawApplication(jobdetails)}>
                            Yes
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                </div>
                )}
              </>
            )}

            {project && isApplyDialogOpen && (
              <Dialog
                open={isApplyDialogOpen}
                onOpenChange={setIsApplyDialogOpen}
              >
                <DialogContent className="max-w-2xl p-0 rounded-xl overflow-hidden">
                  <div className=" max-h-[90vh] overflow-y-auto p-6">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-xl">
                        {jobdetails?.job?.title}
                      </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={null} />
                            <AvatarFallback>
                              {jobdetails?.job?.title
                                ? jobdetails?.job?.title?.charAt(0)
                                : "A"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">
                              {jobdetails?.job?.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {jobdetails?.enterprise?.name}
                            </div>
                          </div>
                        </div>
                        <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200">
                          {jobdetails?.job?.category}
                        </Badge>
                      </div>

                      <Separator />

                      <div>
                        <h3 className="text-lg font-medium mb-2">
                          Project Description
                        </h3>
                        <p className="text-gray-700">{jobdetails?.job?.description}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-medium mb-2">
                          Skills Needed
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {jobdetails?.job?.requiredSkills.map((skill: string, index: number) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="bg-gray-100"
                            >
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Timeline
                          </h3>
                          <p className="text-gray-900">{project?.durationValue} {DURATION_TYPE.find(d => Number(d.value) === project?.durationUnit)?.label}</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Budget
                          </h3>
                          <p className="text-gray-900">$ {jobdetails?.job?.budget}</p>
                        </div>
                        {/* <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Team Size
                          </h3>
                          <p className="text-gray-900">0 members</p>
                        </div> */}
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">
                            Location
                          </h3>
                          <p className="text-gray-900">
                            {jobdetails?.enterprise?.location}
                          </p>
                        </div>
                      </div>

                      <Separator />

                      <FileUploadInput
                        label="Cover letter"
                        name="resume_url"
                        buttonText="Upload cover letter"
                        tooltipText="Upload your cover letter (PDF/DOC, max 5MB)."
                        value={applyJobData.coverLetterFile}
                        onChange={(file: File) =>
                          setApplyJobData({
                            ...applyJobData,
                            coverLetterFile: file,
                          })
                        }
                        onUpload={uploadResumeAPI}
                        progress={progress}
                        toast={toast}
                      />
                      <Separator />
                      <div>
                        <h3 className="text-lg font-medium mb-4">
                          Proposal description
                        </h3>
                        <Textarea
                          placeholder="Explain why you'd be a good fit for this project and what skills you bring to the team..."
                          className="min-h-[120px]"
                          value={applyJobData.coverLetterText}
                          onChange={(e) => {
                            setApplyJobData({
                              ...applyJobData,
                              coverLetterText: e.target.value,
                            });
                          }}
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium mb-4">Proposed Budget</h3>
                        <Input
                          placeholder="Enter your budget"
                          type="number"
                          value={applyJobData.proposedBudget}
                          onChange={(e) => {
                            setApplyJobData({
                              ...applyJobData,
                              proposedBudget: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </div>

                    <DialogFooter className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsApplyDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={() => handleApplyProject()}>
                        Submit Application
                      </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* <Button size="sm">
              <MessageSquare className="mr-1 h-4 w-4" /> Contact Client
            </Button> */}
          </div>
        </div>
      </div>

      {/* Key Stats */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="bg-primary/10 p-2 rounded-full mr-3">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Budget</p>
              <p className="text-lg font-bold">{project.budget}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="bg-orange-100 p-2 rounded-full mr-3">
              <Calendar className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Timeline</p>
              <p className="text-lg font-bold">{project.duration}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="bg-emerald-100 p-2 rounded-full mr-3">
              <Tag className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Category</p>
              <p className="text-lg font-bold">{project.category || "N/A"}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <Eye className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Views</p>
              <p className="text-lg font-bold">{project.views}</p>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Progress Bar */}
      {/* <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
            <div className="mb-2 md:mb-0">
              <h3 className="font-medium">Project Progress</h3>
              <p className="text-sm text-muted-foreground">Deadline: {project.deadline}</p>
            </div>
            <Badge variant="outline" className="font-mono">{project.progress}% Complete</Badge>
          </div>
          <Progress value={project.progress} className="h-2 mt-2" />
        </CardContent>
      </Card> */}

      {/* Main Content Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {/* <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger> */}
        </TabsList>

        {/* Overview Tab */}
        {isEnterpriseOpen ? <Profile isClientProfile={true} newEnterpriseData={enterpriseProfileData} backToProject={backToProject} /> : <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Project Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{jobdetails?.job?.description}</p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Budget</p>
                      <p className="text-sm text-muted-foreground">
                        {jobdetails?.job?.budget}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Duration</p>
                      <p className="text-sm text-muted-foreground">
                        {project?.durationValue} {DURATION_TYPE.find(d => Number(d.value) === project?.durationUnit)?.label}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="text-sm font-medium mb-2">Skills Required</h4>
                  <div className="flex flex-wrap gap-2">
                    {jobdetails?.job?.requiredSkills?.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="bg-muted/50"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Client Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-4 mb-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage
                      src={getImageUrl(jobdetails?.enterprise?.logo)}
                      alt={jobdetails?.enterprise?.companyName.charAt(0)}
                    />
                    <AvatarFallback>
                      {jobdetails?.enterprise?.companyName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-medium">{capitalizeFirst(jobdetails?.enterprise?.companyName)}</h3>
                    <div className="flex items-center mt-1">
                      <div className="flex text-yellow-500">
                        {[...Array(5)].map((_, i) => {
                          const starNumber = i + 1;
                          if (starNumber <= Math.floor(avgRating)) {
                            return (
                              <Star
                                key={i}
                                size={16}
                                className="text-yellow-500"
                                fill="currentColor"
                                stroke="currentColor"
                              />
                            );
                          } else if (starNumber === Math.ceil(avgRating) && avgRating % 1 !== 0) {
                            return (
                              <div
                                key={i}
                                className="relative"
                                style={{ width: 16, height: 16 }}
                              >
                                <Star
                                  size={16}
                                  className="text-gray-300"
                                  fill="none"
                                  stroke="currentColor"
                                />
                                <Star
                                  size={16}
                                  className="text-yellow-500 absolute top-0 left-0"
                                  fill="currentColor"
                                  stroke="currentColor"
                                  style={{
                                    clipPath: "inset(0 50% 0 0)",
                                  }}
                                />
                              </div>
                            );
                          } else {
                            return (
                              <Star
                                key={i}
                                size={16}
                                className="text-gray-300"
                                fill="none"
                                stroke="currentColor"
                              />
                            );
                          }
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Location:</span>
                    <span>{jobdetails?.enterprise?.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Jobs Posted:</span>
                    <span>
                      {jobdetails?.job?.createdAt
                        ? dateFormat(String(jobdetails?.job?.createdAt))
                        : ""}
                      {/* {project.clientInfo.jobsPosted
                        ? dateFormat(String(project.clientInfo.jobsPosted))
                        : ""} */}
                    </span>
                  </div>
                  {/* <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified:</span>
                    <span>{project.clientInfo.verified ? "Yes" : "No"}</span>
                  </div> */}
                </div>
                <Button className="w-full" onClick={() => handleClientProfile(jobdetails?.enterprise)}>View Client Profile</Button>
              </CardContent>
            </Card>
          </div>

          {/* <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Updates</CardTitle>
              <Button variant="outline" size="sm" onClick={handleAddUpdate}>
                <PlusCircle className="h-4 w-4 mr-2" /> Add Update
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projectDetails.updates.map((update) => (
                  <div
                    key={update.id}
                    className="border-l-2 border-muted pl-4 py-1"
                  >
                    <p className="font-medium">{update.content}</p>
                    <p className="text-sm text-muted-foreground">
                      {update.user} - {update.date}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card> */}
        </TabsContent>}

        {/* Tasks Tab */}
        {/* <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Project Tasks</CardTitle>
              <Button size="sm">
                <PlusCircle className="h-4 w-4 mr-2" /> Add Task
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projectDetails.tasks.map(task => (
                  <div key={task.id} className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
                    <div className="flex items-center">
                      {task.status === "completed" ? (
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      ) : task.status === "in-progress" ? (
                        <RefreshCcw className="h-5 w-5 text-blue-500 mr-2" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-500 mr-2" />
                      )}
                      <span>{task.title}</span>
                    </div>
                    <Select 
                      defaultValue={task.status}
                      onValueChange={(value) => handleTaskStatusChange(task.id, value)}
                    >
                      <SelectTrigger className="w-32 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in-progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent> */}

        {/* Team Tab */}
        {/* <TabsContent value="team" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Project Team</CardTitle>
              <Button size="sm">
                <UserPlus className="h-4 w-4 mr-2" /> Add Member
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projectDetails.team.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Avatar>
                        <AvatarImage src={member.avatar} alt={member.name} />
                        <AvatarFallback>{member.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-medium">{member.name}</h3>
                        <p className="text-sm text-muted-foreground">{member.role}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">View Profile</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent> */}

        {/* Files Tab */}
        {/* <TabsContent value="files" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Project Files</CardTitle>
              <Button size="sm">
                <PlusCircle className="h-4 w-4 mr-2" /> Upload File
              </Button>
            </CardHeader>
            <CardContent>
              {project.attachments && project.attachments.length > 0 ? (
                <div className="space-y-3">
                  {project.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-blue-500 mr-3" />
                        <div>
                          <p className="font-medium">{file}</p>
                          <p className="text-xs text-muted-foreground">Added on {project.postedDate}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">Download</Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 mx-auto text-muted-foreground opacity-40" />
                  <p className="mt-2 text-muted-foreground">No files uploaded yet</p>
                  <Button variant="outline" size="sm" className="mt-4">
                    <PlusCircle className="h-4 w-4 mr-2" /> Upload First File
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent> */}

        {/* Messages Tab */}
        {/* <TabsContent value="messages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Conversations</CardTitle>
              <CardDescription>Project communication with the client</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-4 max-h-[400px] overflow-y-auto">
                {projectDetails.conversations.map(msg => (
                  <div 
                    key={msg.id} 
                    className={`p-3 rounded-lg max-w-[80%] ${
                      msg.sender === "Team" 
                        ? "bg-primary/20 ml-auto" 
                        : "bg-muted/50"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">{msg.sender}</span>
                      <span className="text-xs text-muted-foreground">{msg.time}</span>
                    </div>
                    <p>{msg.message}</p>
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <Button onClick={handleSendMessage}>
                  <Send className="h-4 w-4 mr-2" /> Send
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>
    </div>
  );
};

export default ProjectDetails;
